/**
 * Pure cascade-tag math. No React. All exports are total functions safe to
 * call from any component or test.
 *
 * A cascade tag combo is an ordered list of `{key,value}` pairs. A KPI can
 * carry multiple combos in its `rawTags` blob; the picker's filter steps
 * are matched in order and can be locked partially. `parseCascadeTags`
 * normalises the many wire shapes into `Array<Array<{key,value}>>`.
 */

export function parseCascadeTags(raw) {
  if (!raw) return [];

  let parsed = raw;
  if (typeof raw === "string") {
    try {
      parsed = JSON.parse(raw);
    } catch {
      return [];
    }
  }
  if (typeof parsed === "object" && !Array.isArray(parsed)) {
    return Object.values(parsed).filter(Array.isArray);
  }
  if (Array.isArray(parsed)) {
    if (Array.isArray(parsed[0])) return parsed;
    return [parsed];
  }
  return [];
}

export const norm = (v) => (v ?? "").toLowerCase().replace(/\s+/g, "");

/**
 * For a given (kpiItems, filterSteps) pair, return the next filter step's
 * eligible options — the union of unique `{key,values}` reachable from
 * every KPI whose cascade so far matches the active steps.
 */
export function getNextEligibleFilters(kpiItems, filterSteps) {
  const active = filterSteps.filter((s) => s.values?.size);
  const kvm = new Map();

  for (const item of kpiItems) {
    for (const combo of parseCascadeTags(item.rawTags)) {
      if (!combo?.length) continue;

      if (!active.length) {
        const t = combo[0];
        if (t?.key && t?.value) {
          if (!kvm.has(t.key)) kvm.set(t.key, new Set());
          kvm.get(t.key).add(t.value);
        }
        continue;
      }

      let pos = 0;
      let matched = true;
      for (const step of active) {
        if (
          pos >= combo.length ||
          norm(combo[pos].key) !== norm(step.key) ||
          ![...step.values].some((sv) => norm(sv) === norm(combo[pos].value))
        ) {
          matched = false;
          break;
        }
        pos++;
      }

      if (matched && pos < combo.length) {
        const t = combo[pos];
        if (t?.key && t?.value) {
          if (!kvm.has(t.key)) kvm.set(t.key, new Set());
          kvm.get(t.key).add(t.value);
        }
      }
    }
  }

  return Array.from(kvm.entries()).map(([key, values]) => ({
    key,
    values: Array.from(values).sort(),
  }));
}

/**
 * True when at least one of the item's cascade combos satisfies every
 * active filter step in order (values matched at increasing positions).
 * Empty filter set matches everything.
 */
export function kpiMatchesCascadeSteps(item, steps) {
  const active = steps.filter((s) => s.values?.size > 0);
  if (!active.length) return true;

  const combos = parseCascadeTags(item.rawTags);

  return combos.some((combo) => {
    let pos = 0;
    for (const step of active) {
      let found = false;
      for (let i = pos; i < combo.length; i++) {
        if (
          norm(combo[i]?.key) === norm(step.key) &&
          [...step.values].some((sv) => norm(sv) === norm(combo[i]?.value))
        ) {
          pos = i + 1;
          found = true;
          break;
        }
      }
      if (!found) return false;
    }
    return true;
  });
}
